import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import EmployeeForm from "../components/EmployeeForm";
import PageSectionCard from "../components/PageSectionCard";
import { useAppSession } from "../context/AppSessionContext";
import {
  EmployeeFormValues,
  toCreateEmployeePayload,
} from "../lib/employeeForm";
import { fetchAllClientCompanies } from "../lib/clientCompanies";
import { readErrorMessage } from "../lib/http";
import { ClientCompany, Workstation } from "../types/clientCompanies";
import { Employee } from "../types/employees";

const defaultValues: EmployeeFormValues = {
  fullName: "",
  nationalId: "",
  locationType: "",
  clientCompanyId: "",
  workstationId: "",
  email: "",
  phone: "",
  gender: "",
  department: "",
  jobTitle: "",
  seaYears: "",
  landline: "",
  nationality: "",
  note: "",
};

function EmployeeCreatePage() {
  const { authorizedFetch, canMaintainEmployees } = useAppSession();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [autofillMessage, setAutofillMessage] = useState<string | null>(null);
  const [companies, setCompanies] = useState<ClientCompany[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [workstations, setWorkstations] = useState<Workstation[]>([]);
  const [isLoadingWorkstations, setIsLoadingWorkstations] = useState(false);
  const [loadCompaniesError, setLoadCompaniesError] = useState<string | null>(
    null,
  );
  const [loadWorkstationsError, setLoadWorkstationsError] = useState<
    string | null
  >(null);

  const form = useForm<EmployeeFormValues>({ defaultValues });
  const selectedCompanyId = form.watch("clientCompanyId");

  const loadCompanies = useCallback(async () => {
    setIsLoadingCompanies(true);
    setLoadCompaniesError(null);

    try {
      setCompanies(await fetchAllClientCompanies(authorizedFetch));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to load client companies.";
      setLoadCompaniesError(message);
      setCompanies([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  }, [authorizedFetch]);

  useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    form.setValue("workstationId", "");

    if (!selectedCompanyId) {
      setWorkstations([]);
      setLoadWorkstationsError(null);
      return;
    }

    const loadWorkstations = async () => {
      setIsLoadingWorkstations(true);
      setLoadWorkstationsError(null);

      try {
        const response = await authorizedFetch(
          `/client-companies/${selectedCompanyId}/workstations`,
        );

        if (!response.ok) {
          throw new Error(
            await readErrorMessage(
              response,
              `Failed to load workstations (${response.status}).`,
            ),
          );
        }

        setWorkstations((await response.json()) as Workstation[]);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to load workstations.";
        setLoadWorkstationsError(message);
        setWorkstations([]);
      } finally {
        setIsLoadingWorkstations(false);
      }
    };

    void loadWorkstations();
  }, [authorizedFetch, form, selectedCompanyId]);

  const handleNationalIdBlur = useCallback(
    async (nationalId: string) => {
      const cleaned = nationalId.replace(/\D/g, "");
      if (cleaned.length !== 10) {
        setAutofillMessage(null);
        return;
      }

      try {
        const response = await authorizedFetch(
          `/national-registry/lookup/${cleaned}`,
        );

        if (response.status === 404) {
          setAutofillMessage("No National Registry match for this kennitala.");
          return;
        }

        if (!response.ok) {
          setAutofillMessage(null);
          return;
        }

        const person = (await response.json()) as { fullName: string };
        form.setValue("fullName", person.fullName, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setAutofillMessage(
          `Filled name from National Registry: ${person.fullName}`,
        );
      } catch {
        setAutofillMessage(null);
      }
    },
    [authorizedFetch, form],
  );

  const onSubmit = async (values: EmployeeFormValues) => {
    if (!canMaintainEmployees) {
      setSubmitError(
        "Only secretary/manager/admin users can create employees.",
      );
      return;
    }

    setSubmitError(null);

    try {
      const response = await authorizedFetch("/employees", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(toCreateEmployeePayload(values)),
      });

      if (!response.ok) {
        const fallbackMessage =
          response.status === 401 || response.status === 403
            ? "You are not authorized to create employees."
            : response.status === 400
              ? "The selected client company is invalid."
              : response.status === 409
                ? "An employee with this national ID already exists for the selected company."
                : `Failed to create employee (${response.status}).`;

        throw new Error(await readErrorMessage(response, fallbackMessage));
      }

      const created = (await response.json()) as Employee;
      toast.success(`Created employee: ${created.fullName}`);
      navigate("/employees");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create employee.";
      setSubmitError(message);
    }
  };

  const formId = "employee-form";
  const isSubmitting = form.formState.isSubmitting;

  return (
    <PageSectionCard
      titleId="employee-setup-title"
      eyebrow="Employee"
      title="Setup"
      stickyHeader
      actions={
        <button
          className="primaryButton"
          type="submit"
          form={formId}
          disabled={
            isSubmitting ||
            isLoadingCompanies ||
            isLoadingWorkstations ||
            !canMaintainEmployees
          }
        >
          {isSubmitting ? "Creating..." : "Create employee"}
        </button>
      }
    >
      {loadCompaniesError ? (
        <p className="errorMessage" role="alert">
          {loadCompaniesError}
        </p>
      ) : null}

      {loadWorkstationsError ? (
        <p className="errorMessage" role="alert">
          {loadWorkstationsError}
        </p>
      ) : null}

      <EmployeeForm
        id={formId}
        form={form}
        onSubmit={onSubmit}
        submitError={submitError}
        companies={companies}
        workstations={workstations}
        isLoadingCompanies={isLoadingCompanies}
        isLoadingWorkstations={isLoadingWorkstations}
        withPlaceholders
        onNationalIdBlur={handleNationalIdBlur}
        autofillMessage={autofillMessage}
      />
    </PageSectionCard>
  );
}

export default EmployeeCreatePage;
